import { useMemo, useState } from "react";

import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview.types";
import { Collapse } from "@/components/ui";
import {
  DONE_LOOKING,
  GROUP_WORDS,
} from "@/lib/attributeReview/reviewGroupWords";
import {
  duplicateLabelIds,
  groupRows,
  isSettledGroup,
  sameLeafLabels,
} from "@/lib/attributeReview/reviewGroups";
import { requiredWarning } from "@/lib/attributeReview/requiredWarning";
import type { ReviewGroupKey } from "@/lib/attributeReview/reviewRow.types";

import { IgnoreConfirm } from "./IgnoreConfirm";
import { ReviewGroupRows } from "./ReviewGroupRows";
import { ReviewQueueRow } from "./ReviewQueueRow";
import { ReviewBulkBar } from "./ReviewBulkBar";
import { ReviewGroupHeader } from "./ReviewGroupHeader";
import { GroupsToggle } from "./GroupsToggle";

type Props = {
  rows: AttributeReviewMappingRow[];
  variant?: "table" | "compact";
  cursorId?: string | null;
  closed: ReadonlySet<ReviewGroupKey>;
  onToggleGroup: (key: ReviewGroupKey) => void;
  onSetAllGroups: (open: boolean) => void;
  queuedIds: ReadonlySet<string>;
  openIds: ReadonlySet<string>;
  drafts: Record<string, unknown>;
  selectedIds: ReadonlySet<string>;
  onSelectChange: (id: string, checked: boolean, shiftKey: boolean) => void;
  onClearSelection: () => void;
  onApprove: (
    rows: AttributeReviewMappingRow[],
  ) => Promise<{ ok: boolean; error: string | null }>;
  onIgnore: (rows: AttributeReviewMappingRow[]) => Promise<boolean>;
  onUndo: (row: AttributeReviewMappingRow) => Promise<boolean>;
  onShowToSeller: (row: AttributeReviewMappingRow) => Promise<boolean>;
  onOpen: (id: string | null) => void;
};

export function ReviewQueue({
  rows,
  variant = "table",
  cursorId = null,
  closed,
  onToggleGroup,
  onSetAllGroups,
  queuedIds,
  openIds,
  drafts,
  selectedIds,
  onSelectChange,
  onClearSelection,
  onApprove,
  onIgnore,
  onUndo,
  onShowToSeller,
  onOpen,
}: Props) {
  const [confirmRow, setConfirmRow] =
    useState<AttributeReviewMappingRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const groups = useMemo(() => groupRows(rows, queuedIds), [rows, queuedIds]);
  const duplicates = useMemo(() => duplicateLabelIds(rows), [rows]);
  const sameLeaf = useMemo(() => sameLeafLabels(rows), [rows]);
  const selected = useMemo(
    () =>
      groups
        .filter((group) => !isSettledGroup(group.key))
        .flatMap((group) => group.rows)
        .filter((row) => selectedIds.has(String(row.id))),
    [groups, selectedIds],
  );

  const approve = async (target: AttributeReviewMappingRow[]) => {
    const result = await onApprove(target);
    setError(result.ok ? null : result.error);
  };
  const ignore = async (target: AttributeReviewMappingRow[]) => {
    setError((await onIgnore(target)) ? null : "Ignore failed. Try again.");
  };
  const undo = async (row: AttributeReviewMappingRow) => {
    setError((await onUndo(row)) ? null : "Undo failed. Try again.");
  };
  const showToSeller = async (row: AttributeReviewMappingRow) => {
    setError((await onShowToSeller(row)) ? null : "Save failed. Try again.");
  };
  const ignoreOne = (row: AttributeReviewMappingRow) => {
    if (requiredWarning(row.requirementLevel)) setConfirmRow(row);
    else void ignore([row]);
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800">
      {error && (
        <p
          role="alert"
          className="border-b border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
        >
          {error}
        </p>
      )}
      <GroupsToggle
        allOpen={groups.every((group) => !closed.has(group.key))}
        onSetAll={onSetAllGroups}
      />
      {groups.map(({ key, rows: members }) => {
        const open = !closed.has(key);
        const flagsUnsaved = DONE_LOOKING.includes(key);
        const notSaved = flagsUnsaved
          ? members.filter((row) => openIds.has(String(row.id))).length
          : 0;
        return (
          <section key={key}>
            <ReviewGroupHeader
              title={GROUP_WORDS[key].title}
              hint={GROUP_WORDS[key].hint}
              tag={GROUP_WORDS[key].tag}
              count={members.length}
              notSaved={notSaved}
              open={open}
              onToggle={() => onToggleGroup(key)}
            />
            <Collapse open={open}>
              <ReviewGroupRows
                rows={members}
                groupKey={key}
                renderRow={(row) => {
                  const id = String(row.id);
                  return (
                    <ReviewQueueRow
                      key={id}
                      row={row}
                      group={key}
                      variant={variant}
                      checked={selectedIds.has(id)}
                      isCursor={cursorId === id}
                      isDuplicate={duplicates.has(id)}
                      isDraft={id in drafts}
                      notSaved={flagsUnsaved && openIds.has(id)}
                      isQueued={queuedIds.has(id)}
                      sameLeafLabel={sameLeaf.get(id)}
                      onCheck={onSelectChange}
                      onOpen={onOpen}
                      onApprove={(target) => void approve([target])}
                      onIgnore={ignoreOne}
                      onUndo={(target) => void undo(target)}
                      onShowToSeller={(target) => void showToSeller(target)}
                    />
                  );
                }}
              />
            </Collapse>
          </section>
        );
      })}
      <IgnoreConfirm
        row={confirmRow}
        onCancel={() => setConfirmRow(null)}
        onConfirm={(target) => void ignore([target])}
      />
      <ReviewBulkBar
        selected={selected}
        onApprove={(target) => void approve(target)}
        onIgnore={(target) => void ignore(target)}
        onClear={onClearSelection}
      />
    </div>
  );
}
